import React, { useState, useEffect } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { BASE_URL } from '../../constant/api';
import axios from 'axios';
import TermsModal from '../../components/TermsModal/TermsModal';
import AuthBackground from '../../components/Home/AuthBackground/AuthBackground ';
import img from "../../assets/users/logo.png";

// Reusable input component
const CommonInput = ({
  type = "text",
  name,
  value,
  placeholder,
  required = false,
  disabled = false,
  onChange,
  error = false,
}) => {
  return (
    <input
      type={type}
      name={name}
      value={value}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      onChange={onChange}
      className={`border-brand-primary mb-4 w-full rounded-md border p-2 
        ${disabled ? "bg-gray-300 cursor-not-allowed" : "bg-gray-100"
        } ${error ? "border-red-500" : ""}`}
    />
  );
};

// Helper function to format nested error messages
const formatErrorMessages = (errors, parentKey = "") =>
  Object.entries(errors).reduce((messages, [key, value]) => {
    const fullKey = parentKey ? `${parentKey}` : key;
    return messages.concat(
      Array.isArray(value)
        ? `${fullKey.charAt(0).toUpperCase() + fullKey.slice(1)}: ${value.join(", ")}`
        : typeof value === "object" && value !== null
          ? formatErrorMessages(value, fullKey)
          : `${fullKey.charAt(0).toUpperCase() + fullKey.slice(1)}: ${value}`
    );
  }, []);

function Registration() {
  const { register, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [representOrganization, setRepresentOrganization] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);


  // New state to store realtime password validation error message.
  const [passwordError, setPasswordError] = useState("");
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    profile: {
      mobile_number: "",
      address: "",
      city: "",
      post_code: "",
      country: "",
      state: "",
    },
    organization: {
      name: "",
      license_number: "",
      bank_name: "",
      account_number: "",
      ifsc: "",
      tax_number: "",
      address: "",
      location: "",
    },
  });

  const location = useLocation();
  const from = location.state?.from || '/';


  // Regex definitions (also used in the password criteria below)
  const minLengthRegex = /^.{8,}$/;
  const specialCharRegex = /[@#$%!^&*]/;
  const upperCaseRegex = /[A-Z]/;
  const digitRegex = /[0-9]/;

  // Compute the password criteria status based on the current password value.
  // const passwordCriteria = [
  //   { id: 1, label: "At least 8 characters", valid: minLengthRegex.test(formData.password) },
  //   { id: 2, label: "At least one special character (@#$%!^&*)", valid: specialCharRegex.test(formData.password) },
  //   { id: 3, label: "At least one uppercase letter", valid: upperCaseRegex.test(formData.password) },
  //   { id: 4, label: "At least one number", valid: digitRegex.test(formData.password) },
  // ];


  // get all countries and states from the API
  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/accounts/countries/`);
        setCountries(response.data.Data);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (formData.profile.country) {
        try {
          const response = await axios.get(
            `${BASE_URL}/accounts/states/?country=${formData.profile.country}`
          );
          setStates(response.data.Data);
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      }
    };
    fetchStates();
  }, [formData.profile.country]);

  // Update handleChange to handle select inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    const names = name.split('.');

    if (names.length === 1) {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      const [parent, child] = names;
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    }

    // Clear errors
    setFieldErrors(prev => ({ ...prev, [name]: false }));
    setError(null);
  };
  // Realtime password validation effect
  useEffect(() => {
    const { password, confirm_password } = formData;
    let err = "";
    if (password && !minLengthRegex.test(password)) {
      err = "Password must be at least 8 characters long.";
    } else if (password && !upperCaseRegex.test(password)) {
      err = "Password must contain at least one uppercase letter.";
    } else if (password && confirm_password && password !== confirm_password) {
      err = "Passwords do not match.";
    }
    setPasswordError(err);
  }, [formData.password, formData.confirm_password]);

  // Step 1: Validate basic user details.
  const validateStep1 = () => {
    let errors = {};
    if (!formData.first_name.trim()) errors.first_name = true;
    if (!formData.last_name.trim()) errors.last_name = true;
    if (!formData.email.trim()) errors.email = true;
    if (!formData.profile.mobile_number.trim()) errors["profile.mobile_number"] = true;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError("Please fill in all fields");
      return false;
    }
    setFieldErrors({});
    return true;
  };

  // Step 2: Validate organization details.
  const validateStep2 = () => {
    if (representOrganization) {
      const org = formData.organization;
      const allEmpty = Object.values(org).every(value => value.trim() === "");
      if (allEmpty) {
        setFieldErrors({});
        return true;
      } else {
        let errors = {};
        if (!org.name.trim()) errors["organization.name"] = true;
        if (!org.license_number.trim()) errors["organization.license_number"] = true;
        if (!org.bank_name.trim()) errors["organization.bank_name"] = true;
        if (!org.account_number.trim()) errors["organization.account_number"] = true;
        if (!org.ifsc.trim()) errors["organization.ifsc"] = true;
        if (!org.tax_number.trim()) errors["organization.tax_number"] = true;
        if (!org.address.trim()) errors["organization.address"] = true;
        if (!org.location.trim()) errors["organization.location"] = true;

        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setError("Please fill all organization details or leave all fields empty to skip");
          return false;
        }
      }
    }
    setFieldErrors({});
    return true;
  };

  // Step 3: Validate profile details and passwords.
  const validateStep3 = () => {
    let errors = {};
    const { password, confirm_password, profile } = formData;

    if (!profile.country.trim()) errors["profile.country"] = true;
    if (!profile.state.trim()) errors["profile.state"] = true;
    if (!profile.city.trim()) errors["profile.city"] = true;
    if (!profile.post_code.trim()) errors["profile.post_code"] = true;
    if (!profile.address.trim()) errors["profile.address"] = true;
    if (!password.trim()) errors.password = true;
    if (!confirm_password.trim()) errors.confirm_password = true;

    if (password && confirm_password && password !== confirm_password) {
      errors.password = true;
      errors.confirm_password = true;
      setError("Passwords do not match");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (!error) setError("Please fill in all profile details");
      return "error";
    }
    setFieldErrors({});
    return null;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // If the user chooses to skip organization details,
  // clear the organization data before moving to Step 3.
  const handleSkipOrganization = () => {
    setFormData(prev => ({
      ...prev,
      organization: {
        name: "",
        license_number: "",
        account_details: "",
        tax_number: "",
        address: "",
        location: ""
      }
    }));
    setCurrentStep(3);
  };

  // Updated handleSubmit to only include organization data if applicable.
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateStep3();
    if (validationError) return setError(validationError);
    if (passwordError) return setError(passwordError);
    if (!termsAccepted) return setError("You must agree to the terms & policy.");

    setIsLoading(true);

    // Build the payload with the required fields
    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      password: formData.password,
      confirm_password: formData.confirm_password,
      profile: formData.profile,
    };

    // Add organization only if the user is representing one.
    if (representOrganization) {
      payload.organization = {
        name: formData.organization.name,
        license_number: formData.organization.license_number,
        bank_name: formData.organization.bank_name,
        account_number: formData.organization.account_number,
        ifsc: formData.organization.ifsc,
        tax_number: formData.organization.tax_number,
        address: formData.organization.address,
        location: formData.organization.location,
      };
    }

    try {
      const res = await register(payload);

      console.log("API Response:", res); // Debugging step

      if (res.status) {
        setSuccessMessage(res.message);
      } else {
        if (typeof res.message === "object") {
          const errorMessages = formatErrorMessages(res.message).join("\n");
          setError(errorMessages);
        } else {
          setError(res.message);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // if (isAuthenticated) {
  //   return <Navigate to={from} />;
  // }

  return (
    <div className="grid h-screen w-screen grid-cols-1 md:grid-cols-2">
      <AuthBackground />

      <div className="flex flex-col items-center justify-center p-4 md:p-4 md:h-full">
        <div className="flex w-full sm:w-[450px] flex-col items-start border-0 md:border-2 border-solid px-5 md:p-10 rounded-xl">
          <p className="text-start py-2 font-semibold text-3xl md:text-2xl">
            {currentStep === 1
              ? "Create your account"
              : currentStep === 2
                ? "Organization Details"
                : "Complete Your Profile"}
          </p>

          <hr className="border-1 my-8 w-full border-solid border-graylight" />

          {currentStep === 1 && (
            <div className="w-full">
              <CommonInput
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="First Name"
                required
                error={fieldErrors.first_name}
              />
              <CommonInput
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Last Name"
                required
                error={fieldErrors.last_name}
              />
              <CommonInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                required
                error={fieldErrors.email}
              />
              <CommonInput
                type="text"
                name="profile.mobile_number"
                value={formData.profile.mobile_number}
                onChange={handleChange}
                placeholder="Mobile Number"
                required
                error={fieldErrors["profile.mobile_number"]}
              />
              <button
                onClick={handleNextStep}
                className="w-full rounded-xl px-6 font-semibold py-2 text-black transition duration-200 bg-[#FFAE00]"
              >
                Next
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="w-full">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={representOrganization}
                  onChange={(e) => setRepresentOrganization(e.target.checked)}
                  className="mr-2"
                />
                <label className="text-sm text-[#FFAE00]">
                  Representing an organization? Fill details below
                </label>
              </div>

              <CommonInput
                type="text"
                name="organization.name"
                value={formData.organization.name}
                onChange={handleChange}
                placeholder="Organization Name"
                disabled={!representOrganization}
                error={fieldErrors["organization.name"]}
              />
              <CommonInput
                type="text"
                name="organization.license_number"
                value={formData.organization.license_number}
                onChange={handleChange}
                placeholder="License Number"
                disabled={!representOrganization}
                error={fieldErrors["organization.license_number"]}
              />
              <CommonInput
                type="text"
                name="organization.bank_name"
                value={formData.organization.bank_name}
                onChange={handleChange}
                placeholder="Bank Name"
                disabled={!representOrganization}
                error={fieldErrors["organization.bank_name"]}
              />
              <CommonInput
                type="text"
                name="organization.account_number"
                value={formData.organization.account_number}
                onChange={handleChange}
                placeholder="Account Number"
                disabled={!representOrganization}
                error={fieldErrors["organization.account_number"]}
              />
              <CommonInput
                type="text"
                name="organization.ifsc"
                value={formData.organization.ifsc}
                onChange={handleChange}
                placeholder="IFSC Code"
                disabled={!representOrganization}
                error={fieldErrors["organization.ifsc"]}
              />
              <CommonInput
                type="text"
                name="organization.tax_number"
                value={formData.organization.tax_number}
                onChange={handleChange}
                placeholder="Tax Number"
                disabled={!representOrganization}
                error={fieldErrors["organization.tax_number"]}
              />
              <CommonInput
                type="text"
                name="organization.address"
                value={formData.organization.address}
                onChange={handleChange}
                placeholder="Organization Address"
                disabled={!representOrganization}
                error={fieldErrors["organization.address"]}
              />
              <CommonInput
                type="text"
                name="organization.location"
                value={formData.organization.location}
                onChange={handleChange}
                placeholder="Location"
                disabled={!representOrganization}
                error={fieldErrors["organization.location"]}
              />

              <button
                onClick={handleSkipOrganization}
                className="w-full rounded-xl px-6 font-semibold py-2 text-black transition duration-200 bg-[#FFAE00] col-span-2"
              >
                Skip
              </button>
              <div className="flex gap-4 mt-4">
                <button
                  onClick={handlePrevStep}
                  className="w-full rounded-xl px-6 font-semibold py-2 transition duration-200 bg-white border border-gray-300 text-gray-500"
                >
                  Back
                </button>
                <button
                  onClick={handleNextStep}
                  className="w-full rounded-xl px-6 font-semibold py-2 transition duration-200 bg-white border border-gray-300 text-gray-500"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <form className="w-full" onSubmit={handleSubmit}>
              <select
                name="profile.country"
                value={formData.profile.country}
                onChange={handleChange}
                className={`border-brand-primary bg-gray-100 mb-4 w-full rounded-md border p-2 ${fieldErrors["profile.country"] ? "border-red-500" : ""
                  }`}
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>

              <div className='grid grid-cols-2 gap-4 my-'>
                <select
                  name="profile.state"
                  value={formData.profile.state}
                  onChange={handleChange}
                  className={`border-brand-primary bg-gray-100 w-full rounded-md border p-2 h-[42px] ${ // Set explicit height
                    fieldErrors["profile.state"] ? "border-red-500" : ""
                    }`}
                  required
                  disabled={!formData.profile.country}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>

                <CommonInput
                  type="text"
                  name="profile.city"
                  value={formData.profile.city}
                  onChange={handleChange}
                  placeholder="City"
                  required
                  error={fieldErrors["profile.city"]}
                  className="h-[42px]" // Add explicit height to CommonInput
                />
              </div>

              <CommonInput
                type="text"
                name="profile.post_code"
                value={formData.profile.post_code}
                onChange={handleChange}
                placeholder="Post Code"
                required
                error={fieldErrors["profile.post_code"]}
              />
              <CommonInput
                type="text"
                name="profile.address"
                value={formData.profile.address}
                onChange={handleChange}
                placeholder="Address"
                required
                error={fieldErrors["profile.address"]}
              />

              {/* Password input with toggle */}
              <div className="relative mb-4">
                <CommonInput
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password"
                  required
                  error={fieldErrors.password}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 bottom-4 flex items-center pr-2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {/* Display dynamic password criteria */}
              <ul className="mt-1 mb-4">
                <p className='text-xs'>Password must be minimum <span className='text-[#FFAE00]'>8 characters</span> including <span className='text-[#FFAE00]'>uppercase</span></p>
                {/* {passwordCriteria.map((criteria) => (
                  <li
                    key={criteria.id}
                    className={`text-xs ${criteria.valid ? 'text-green-500' : 'text-red-500'}`}
                  >
                    {criteria.label}
                  </li>
                ))} */}
              </ul>

              {passwordError && (
                <p className="text-red-500 text-sm mb-2">{passwordError}</p>
              )}

              {/* Confirm Password input with toggle */}
              <div className="relative">
                <CommonInput
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  placeholder="Confirm Password"
                  required
                  error={fieldErrors.confirm_password}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 bottom-4 flex items-center pr-2"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  className="mr-2"
                />
                <label className="text-sm">
                  I agree to the
                  <span
                    className="text-brand-primary cursor-pointer"
                    onClick={() => setIsTermsOpen(true)}
                  >{" "}terms & policy</span>
                </label>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="w-full rounded-xl px-6 font-semibold py-2 transition bg-white border border-gray-300 text-gray-500"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="w-full rounded-xl px-6 font-semibold py-2 text-black transition duration-200 bg-[#FFAE00]"
                  disabled={isLoading}
                >
                  {isLoading ? "Processing..." : "Sign Up"}
                </button>
              </div>

              {error && (
                <p className="text-red-500 text-sm mt-2 whitespace-pre-wrap">
                  {error}
                </p>
              )}
              {successMessage && (
                <p className="text-green-500 text-sm mt-2">
                  {successMessage}
                </p>
              )}

              {/* <p className="text-center text-sm mt-4">
                Already have an account?{" "}
                <Link to="/login" className="cursor-pointer font-semibold underline text-brand-primary">
                  LOGIN
                </Link>
              </p> */}
            </form>
          )}
          <div className='flex justify-start w-full'>
            <p className="text-center text-sm my-2">
              Have an account?{" "}
              <span className="cursor-pointer font-semibold text-[#FFAE00]">
                <Link to="/login/email">Sign In</Link>
              </span>
            </p>
          </div>
          {/* Footer Logo */}
          <div className="my-5 flex justify-center items-center w-full">
            <img src={img} alt="Bricks n Mix" className="h-12" />
          </div>

        </div>
      </div>
      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </div>
  );
}

export default Registration;
